import { computed, ref } from '@vue/composition-api';
import { groupKanbanOperations } from '@/enums';
import useCandidates from '@/compositions/useCandidates';
import Candidate from '@/orm/models/Candidate/Candidate';
import useValidation from '@/compositions/useValidation';
import useRecaptcha from '@/compositions/useRecaptcha';
import { compileNotificationTemplate } from '@/functions';
import useUser from '@/compositions/useUser';
import useVacancies from '@/compositions/useVacancies';
import useAccount from '@/compositions/accounting/useAccount';

const selectedCandidates = ref([]);
const operationsEnabled = ref(false);

export function useKanbanMultipleOperations() {
  const { localLoading, withLoading } = useValidation();
  const { quota } = useAccount();
  const { sendNotifyMass, updateStatusMass } = useCandidates();

  function selectCandidate(id, value) {
    if (!value) {
      selectedCandidates.value = selectedCandidates.value.filter((val) => val !== id);
    } else {
      selectedCandidates.value.push(id);
    }
  }

  function toggleCandidate(id) {
    if (selectedCandidates.value.some((candidateId) => candidateId === id)) {
      selectedCandidates.value = selectedCandidates.value.filter((val) => val !== id);
    } else {
      selectedCandidates.value.push(id);
    }
  }

  function selectMultipleCandidates(ids, value) {
    let oldCandidates = [...selectedCandidates.value];

    ids.forEach((id) => {
      if (!value) {
        oldCandidates = oldCandidates.filter((val) => val !== id);
      } else {
        oldCandidates.push(id);
      }
    });
    selectedCandidates.value = oldCandidates;
  }

  function toggleOperationsEnabled() {
    operationsEnabled.value = !operationsEnabled.value;
    selectedCandidates.value = [];
  }

  async function compileMessage(template, candidateId) {
    const { findCandidateById } = useCandidates();
    const candidate = findCandidateById(candidateId);
    const { currentUser } = useUser();
    const { findVacancyById } = useVacancies();
    const vacancy = findVacancyById(candidate.vacancyId);
    return compileNotificationTemplate(
      template,
      currentUser.value,
      currentUser.value.company,
      vacancy,
      candidate,
    );
  }

  async function sendEmailToCandidate(template, ids) {
    const compiled = await compileMessage(template, ids[0]);
    const { getRecaptchaToken } = useRecaptcha();
    const recaptchaToken = await getRecaptchaToken('candidateContact');
    const payload = { recaptchaToken };
    payload.mail = compiled;
    return withLoading(async () => {
      try {
        if (ids.length > 1) {
          payload.candidateIds = [...ids];
          await sendNotifyMass(payload);
          return;
        }
        await Candidate.api().notify(ids[0], payload);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('error notify email:', e);
        }
      }
    });
  }

  async function sendSmsToCandidate(template, ids) {
    const { getRecaptchaToken } = useRecaptcha();
    const recaptchaToken = await getRecaptchaToken('candidateContact');
    const payload = { recaptchaToken };
    payload.sms = template;
    return withLoading(async () => {
      try {
        if (ids.length > 1) {
          payload.candidateIds = [...ids];
          await sendNotifyMass(payload);
          return;
        }
        await Candidate.api().notify(ids[0], payload);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('error notify sms:', e);
        }
      }
    });
  }

  async function sendEmail(template) {
    return sendEmailToCandidate(template, selectedCandidates.value);
  }

  async function sendSms(template) {
    return sendSmsToCandidate(template, selectedCandidates.value);
  }

  async function updateStatusCandidate(status) {
    const { updateStatus } = useCandidates();
    return withLoading(async () => {
      try {
        if (selectedCandidates.value.length > 1) {
          await updateStatusMass({
            status,
            candidateIds: selectedCandidates.value,
          });
          return;
        }
        await updateStatus(selectedCandidates.value[0], status);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('error update statuses:', e);
        }
      }
    });
  }

  async function applyAction(operation) {
    await withLoading(async () => {
      switch (operation.type) {
        case groupKanbanOperations.MOVE:
          await updateStatusCandidate(operation.payload.status);
          break;
        case groupKanbanOperations.SEND_EMAIL:
          await sendEmail(operation.payload.template);
          break;
        case groupKanbanOperations.SEND_SMS:
          await sendSms(operation.payload.template);
          break;
        default:
          throw new Error('Not Implemented');
      }
    });
  }

  const canSendSms = computed(() => {
    return quota.value?.sms >= selectedCandidates.value.length;
  });

  function clearSelected() {
    selectedCandidates.value = [];
    operationsEnabled.value = false;
  }

  return {
    selectedCandidates,
    enabled: computed(() => !!selectedCandidates.value.length),
    operationsEnabled,
    toggleOperationsEnabled,
    selectCandidate,
    selectMultipleCandidates,
    applyAction,
    isCandidateSelected: (id) => {
      return selectedCandidates.value.some((val) => id === val);
    },
    sendEmail,
    toggleCandidate,
    localLoading,
    canSendSms,
    clearSelected,
  };
}
